import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { api, setAuthIdentity, setTokenRefresher, setOnAuthFailure, type User } from '../lib/api';

/**
 * Google ID tokens live ~1 hour. We preemptively trigger a silent One-Tap
 * renewal this many ms before `exp`, so the user never sees the login screen
 * mid-session.
 */
const REFRESH_LEAD_MS = 2 * 60 * 1000;

/** Decode the JWT `exp` claim (seconds since epoch) — returns ms, or null if unparseable. */
function tokenExpiryMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // Google JWTs use base64url (no padding, - and _ instead of + and /).
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch { return null; }
}

// ─── Silent token refresh (SSO mode only) ────────────────────────────────────
//
// This component MUST only be mounted inside <GoogleOAuthProvider>. It's
// separate from AuthProvider so the provider can be used in dev mode (no SSO,
// no provider) without crashing on the One-Tap hook.
//
// Two triggers:
//   1. Preemptive timer — fires ~2 min before the current token's exp, so an
//      active session never sees the token expire.
//   2. Imperative — api.ts calls the refresher on any 401 with a Google
//      Bearer token. Useful when the tab was idle past the preemptive timer,
//      or when the timer fired but Google declined the silent sign-in.

// Module-level pending refresh — there's at most one in flight. Multiple
// concurrent 401s share the same promise so we don't spam Google.
interface PendingRefresh { resolve: (token: string | null) => void }
let pendingRefresh: PendingRefresh | null = null;
const refreshListeners = new Set<() => void>();

// Separate channel for "a new token has just been stored". Fires from both
// the initial sign-in AND each silent refresh, so GoogleSilentRefresh can
// re-schedule its preemptive timer against the NEW token's exp. Before this
// existed, the timer only scheduled once on mount — fine for the first
// refresh, broken for every subsequent expiration. Active sessions would
// silently drift into 401-land ~2h in.
const tokenChangeListeners = new Set<() => void>();
function notifyTokenChanged() {
  tokenChangeListeners.forEach((l) => l());
}

/**
 * How long to wait for Google's silent One-Tap to resolve before giving
 * up and bouncing to the login screen. Empirically Google rejects (FedCM
 * blocked, no session, third-party cookies disabled, …) in 1–2s — when
 * it can't refresh, it bails fast. The previous 30s ceiling was wishful
 * thinking and meant operators stared at a non-responsive page for half
 * a minute every time the tab woke up post-expiry.
 *
 * 6 seconds covers any realistic legit-success path (success usually
 * lands in <2s) plus a safety pad, and keeps the perceived hang under
 * the threshold where users start hammering F5.
 */
const SILENT_REFRESH_TIMEOUT_MS = 6_000;

function requestRefresh(): Promise<string | null> {
  if (pendingRefresh) {
    // Piggy-back on the existing request — we'll receive the same resolution.
    return new Promise<string | null>((resolve) => {
      const prev = pendingRefresh!.resolve;
      pendingRefresh!.resolve = (t) => { prev(t); resolve(t); };
    });
  }
  return new Promise<string | null>((resolve) => {
    pendingRefresh = { resolve };
    // Nudge every mounted GoogleSilentRefresh to fire useGoogleOneTapLogin.
    refreshListeners.forEach((l) => l());
    setTimeout(() => {
      if (pendingRefresh) {
        const d = pendingRefresh;
        pendingRefresh = null;
        d.resolve(null);
      }
    }, SILENT_REFRESH_TIMEOUT_MS);
  });
}

function resolvePendingRefresh(token: string | null) {
  if (pendingRefresh) {
    const d = pendingRefresh;
    pendingRefresh = null;
    d.resolve(token);
  }
}

export function GoogleSilentRefresh() {
  const [refreshRequested, setRefreshRequested] = useState(false);

  // Register with api.ts so 401s trigger a refresh.
  useEffect(() => {
    setTokenRefresher(requestRefresh);
    return () => setTokenRefresher(null);
  }, []);

  // Subscribe to imperative refresh requests coming from api.ts.
  useEffect(() => {
    const listener = () => setRefreshRequested(true);
    refreshListeners.add(listener);
    return () => { refreshListeners.delete(listener); };
  }, []);

  // Preemptive refresh timer. Re-schedules on every token change so each
  // successive refresh sets up the NEXT one. Without the listener the timer
  // only ran once per mount and sessions silently died after the second
  // expiration.
  const [tokenGen, setTokenGen] = useState(0);
  useEffect(() => {
    const bump = () => setTokenGen((n) => n + 1);
    tokenChangeListeners.add(bump);
    return () => { tokenChangeListeners.delete(bump); };
  }, []);
  useEffect(() => {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem(SS_GOOGLE_TOKEN) : null;
    if (!token) return;
    const expMs = tokenExpiryMs(token);
    if (!expMs) return;
    const delay = Math.max(0, expMs - REFRESH_LEAD_MS - Date.now());
    const timer = window.setTimeout(() => setRefreshRequested(true), delay);
    return () => window.clearTimeout(timer);
  }, [tokenGen]);

  // Background tabs throttle setTimeout heavily (Chrome clamps to 1 minute),
  // so a preemptive timer scheduled while the tab was hidden can fire well
  // past the exp boundary. When the tab becomes visible again, check if we're
  // already past the lead time and fire the refresh immediately — otherwise
  // the user's first click on return hits a 401 and only then does the retry
  // path kick in, which is user-visible as "stalled clicks for a few seconds".
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const token = sessionStorage.getItem(SS_GOOGLE_TOKEN);
      if (!token) return;
      const expMs = tokenExpiryMs(token);
      if (!expMs) return;
      if (expMs - REFRESH_LEAD_MS - Date.now() <= 0) setRefreshRequested(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useGoogleOneTapLogin({
    disabled: !refreshRequested,
    onSuccess: (resp) => {
      setRefreshRequested(false);
      const token = resp.credential ?? null;
      const handler = (window as unknown as Record<string, unknown>).__orpHandleGoogleLogin;
      if (typeof handler === 'function') (handler as (r: typeof resp) => void)(resp);
      // handleGoogleLogin stores the token and fires notifyTokenChanged;
      // that causes THIS component's preemptive timer to re-schedule against
      // the new token's exp, which is what keeps refresh cycles chaining.
      resolvePendingRefresh(token);
    },
    onError: () => {
      setRefreshRequested(false);
      resolvePendingRefresh(null);
    },
    auto_select: true,
    cancel_on_tap_outside: false,
  });

  return null;
}

// ─── Configuration ──────────────────────────────────────────────────────────

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
const IS_SSO_ENABLED = !!GOOGLE_CLIENT_ID;

// SessionStorage keys — per-tab so multiple tabs don't collide.
const SS_REAL_USER_ID = 'orp.realUserId';
const SS_IMPERSONATE_ID = 'orp.impersonateUserId';
const SS_GOOGLE_TOKEN = 'orp.googleIdToken';

// ─── Dev stub users (only used when SSO is NOT configured) ──────────────────

// fpa_role on the dev personas mirrors the v1 policy (explicit grant, execs).
// COO is an approver (can manage FP&A access); CTO is a viewer. Everyone else
// is NULL — including the admins above them are the same execs, so there's no
// "admin without FP&A" persona here; that divergence is proven by backend tests.
export const DEV_USERS: User[] = [
  { jira_user_id: 'u-dev-1',  display_name: 'Alice Chen (Developer)',         email: 'alice@example.com', role: 'IC',       title: 'Developer',                team: 'Development',           manager_jira_user_id: 'u-dir-dev', is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-is-1',   display_name: 'Ben Okafor (Impl Specialist)',    email: 'ben@example.com',   role: 'IC',       title: 'Implementation Specialist',team: 'Client Enablement',     manager_jira_user_id: 'u-dir-ce',  is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-dm-1',   display_name: 'Dana Rivers (DM)',                email: 'dana@example.com',  role: 'DM',       title: 'Delivery Manager',         team: 'Delivery',              manager_jira_user_id: 'u-dir-del', is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-ce-lead',display_name: 'Aisha Osei (CE Lead)',            email: 'aisha@example.com', role: 'DM',       title: 'Client Enablement Lead',   team: 'Client Enablement',     manager_jira_user_id: 'u-dir-ce',  is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-sa-1',   display_name: 'Evan Torres (SA)',                email: 'evan@example.com',  role: 'SA',       title: 'Solution Architect',       team: 'Solution Architecture', manager_jira_user_id: 'u-dir-sa',  is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-dir-del',display_name: 'Sasha Kim (Director of Delivery)',email: 'sasha@example.com', role: 'DIRECTOR', title: 'Director of Delivery',     team: 'Delivery',              manager_jira_user_id: 'u-coo',     is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-dir-dev',display_name: 'Taylor Brooks (Director of Dev)', email: 'taylor@example.com',role: 'DIRECTOR', title: 'Director of Development',  team: 'Development',           manager_jira_user_id: 'u-cto',     is_admin: false, is_billing_admin: false, fpa_role: null,       is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-coo',    display_name: 'Morgan Lee (COO)',                email: 'coo@example.com',   role: 'EXEC',     title: 'COO',                      team: 'Leadership',            manager_jira_user_id: null,        is_admin: true,  is_billing_admin: true,  fpa_role: 'approver', is_active: true, bonus_role: null, chargeover_user_id: null },
  { jira_user_id: 'u-cto',    display_name: 'Kai Nakamura (CTO)',              email: 'cto@example.com',   role: 'EXEC',     title: 'CTO',                      team: 'Leadership',            manager_jira_user_id: null,        is_admin: true,  is_billing_admin: true,  fpa_role: 'viewer',   is_active: true, bonus_role: null, chargeover_user_id: null },
];

function readDevUser(id: string | null): User | null {
  if (!id) return null;
  return DEV_USERS.find((u) => u.jira_user_id === id) ?? null;
}

// ─── Context shape ──────────────────────────────────────────────────────────

interface AuthContextValue {
  /** The effective user — impersonated user if impersonating, else real user. */
  user: User | null;
  /** The real signed-in user (always, regardless of impersonation). */
  realUser: User | null;
  /** True when the real user is viewing as someone else. */
  isImpersonating: boolean;
  isLoading: boolean;
  /** True when SSO is configured and user needs to sign in. */
  needsLogin: boolean;
  /** True when SSO is configured (production mode). */
  isSsoEnabled: boolean;
  /** True when the user was just bounced out by a mid-session 401 (token
   *  expired and silent refresh couldn't recover). Clears on next sign-in.
   *  Used by LoginScreen to explain *why* the user landed back here instead
   *  of leaving them to guess. */
  sessionExpired: boolean;
  switchUser: (user: User) => void; // dev-only persona switcher
  startImpersonation: (target: User) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  // --- Dev persona state (only meaningful when SSO is off) ---
  const [devUser, setDevUser] = useState<User>(() => {
    if (IS_SSO_ENABLED || typeof window === 'undefined') return DEV_USERS[0];
    return readDevUser(sessionStorage.getItem(SS_REAL_USER_ID)) ?? DEV_USERS[0];
  });

  // --- SSO state (only meaningful when SSO is on) ---
  const [ssoUser, setSsoUser] = useState<User | null>(null);
  const [ssoLoading, setSsoLoading] = useState(IS_SSO_ENABLED);
  const [googleToken, setGoogleToken] = useState<string | null>(() => {
    if (!IS_SSO_ENABLED || typeof window === 'undefined') return null;
    return sessionStorage.getItem(SS_GOOGLE_TOKEN);
  });

  // --- Impersonation (shared) ---
  const [impersonating, setImpersonating] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    if (IS_SSO_ENABLED) return null; // restored after SSO bootstrap
    return readDevUser(sessionStorage.getItem(SS_IMPERSONATE_ID));
  });

  // True when a mid-session 401 dropped us back to the login screen. Set by
  // the onAuthFailure handler below; cleared on a fresh sign-in.
  const [sessionExpired, setSessionExpired] = useState(false);

  // The effective user as resolved by /auth/me — the ONLY source that carries
  // `permissions` (and `is_sales`). The `impersonating`/`devUser` objects are
  // bare user rows (users list, forecast drill-ins, dev stubs) without those
  // fields, so anything permission-gated would vanish while impersonating if
  // we rendered from them directly. Hydrated by the effect below whenever the
  // effective identity changes.
  const [resolvedUser, setResolvedUser] = useState<User | null>(null);

  // Derive the real user from whichever auth mode is active.
  const realUser = IS_SSO_ENABLED ? ssoUser : devUser;
  const effectiveId = impersonating?.jira_user_id ?? realUser?.jira_user_id ?? null;
  // Prefer the hydrated user; fall back to the bare row until /auth/me lands
  // (or if it fails — server-side gates still hold). The id guard drops a
  // stale resolution the moment the identity changes, instead of rendering
  // the previous identity's access for a frame.
  const user = (resolvedUser?.jira_user_id === effectiveId ? resolvedUser : null)
    ?? impersonating ?? realUser;
  const isLoading = IS_SSO_ENABLED ? ssoLoading : false;
  const needsLogin = IS_SSO_ENABLED && !ssoLoading && !ssoUser;

  // --- Keep the API client's auth headers in sync ---
  useEffect(() => {
    if (IS_SSO_ENABLED) {
      setAuthIdentity({
        userId: null, // SSO mode doesn't use X-Dev-User-Id
        impersonating: impersonating?.jira_user_id ?? null,
        googleToken,
      });
    } else {
      setAuthIdentity({
        userId: realUser?.jira_user_id ?? null,
        impersonating: impersonating?.jira_user_id ?? null,
        googleToken: null,
      });
    }
  }, [realUser, impersonating, googleToken]);

  // --- Hydrate the effective user through /auth/me ---
  // Declared AFTER the header-sync effect above so the fetch carries the new
  // identity headers (effects run in declaration order within a commit).
  // Fires on impersonation start/stop and dev-persona switches; the server
  // resolves the impersonation target's effective permission set.
  useEffect(() => {
    if (!effectiveId) { setResolvedUser(null); return; }
    if (IS_SSO_ENABLED && !googleToken) return; // not signed in yet
    let cancelled = false;
    api.auth.me()
      .then(({ user: u }) => { if (!cancelled) setResolvedUser(u); })
      .catch(() => { if (!cancelled) setResolvedUser(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId, googleToken]);

  // --- Persist dev state ---
  useEffect(() => {
    if (IS_SSO_ENABLED || typeof window === 'undefined') return;
    sessionStorage.setItem(SS_REAL_USER_ID, devUser.jira_user_id);
    if (impersonating) sessionStorage.setItem(SS_IMPERSONATE_ID, impersonating.jira_user_id);
    else sessionStorage.removeItem(SS_IMPERSONATE_ID);
  }, [devUser, impersonating]);

  // --- SSO bootstrap: validate stored token on mount ---
  useEffect(() => {
    if (!IS_SSO_ENABLED || !googleToken) {
      setSsoLoading(false);
      return;
    }
    // Validate the token against the backend; if it's expired the backend
    // will return 401 and we'll show the login button.
    api.auth.me()
      .then(({ user: u }) => { setSsoUser(u); setSsoLoading(false); })
      .catch(() => {
        sessionStorage.removeItem(SS_GOOGLE_TOKEN);
        setGoogleToken(null);
        setSsoLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent token refresh is handled by <GoogleSilentRefresh/>, which is only
  // mounted in SSO mode (inside <GoogleOAuthProvider/>). Doing it here would
  // crash dev mode where the OAuth provider isn't rendered at all.

  // Register a hard-stop handler with the API client: if any in-flight
  // request lands a final 401 (silent refresh exhausted, or FedCM was blocked
  // and never got to retry), drop the stored token so the LoginScreen takes
  // over. Without this the page just sits with a half-loaded shell + an
  // opaque "HTTP 401" error and the user has no way back in without F5.
  //
  // Re-registers on every ssoUser change. setOnAuthFailure resets the
  // `authFailureFired` latch inside api.ts each time it's called, so a
  // fresh sign-in re-arms the handler for the next session-expiry. Without
  // the re-registration the second token expiry in a session would fall
  // off the cliff — the latch would already be true from the first
  // expiry and onAuthFailure wouldn't fire again, leaving the page hung
  // with a 401 the user couldn't see.
  useEffect(() => {
    if (!IS_SSO_ENABLED) return;
    setOnAuthFailure(() => {
      sessionStorage.removeItem(SS_GOOGLE_TOKEN);
      setGoogleToken(null);
      setSsoUser(null);
      setImpersonating(null);
      setSessionExpired(true);
    });
    return () => setOnAuthFailure(null);
  }, [ssoUser]);

  // --- Exposed to the Google login callback (see App.tsx) ---
  const handleGoogleLogin = useCallback((credentialResponse: { credential?: string }) => {
    const token = credentialResponse.credential;
    if (!token) return;
    sessionStorage.setItem(SS_GOOGLE_TOKEN, token);
    setGoogleToken(token);
    // Immediately sync the header so the /auth/me call uses the new token,
    // AND notify GoogleSilentRefresh so it schedules the NEXT preemptive
    // refresh against the new exp. Without this the timer only ran once per
    // page load and the second expiration (~2h in) killed the session.
    setAuthIdentity({ userId: null, impersonating: null, googleToken: token });
    notifyTokenChanged();

    // Only call /auth/me if we don't already have a user bound — the silent
    // refresh path re-issues the same-email token and doesn't need a
    // round-trip. The sign-in-from-scratch path does.
    // Fresh sign-in clears the "session expired" banner, which is set by
    // the auth-failure trap on the previous session's 401.
    setSessionExpired(false);
    if (ssoUser) return;
    setSsoLoading(true);
    api.auth.me()
      .then(({ user: u }) => { setSsoUser(u); setSsoLoading(false); })
      .catch((err) => {
        alert(`Sign-in failed: ${(err as Error).message}`);
        sessionStorage.removeItem(SS_GOOGLE_TOKEN);
        setGoogleToken(null);
        setSsoLoading(false);
      });
  }, [ssoUser]);

  // Expose on the window so App.tsx can pass it to GoogleLogin without
  // prop-drilling through the provider boundary.
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__orpHandleGoogleLogin = handleGoogleLogin;
    return () => { delete (window as unknown as Record<string, unknown>).__orpHandleGoogleLogin; };
  }, [handleGoogleLogin]);

  const switchUser = (u: User) => {
    setImpersonating(null);
    setDevUser(u);
  };

  const startImpersonation = async (target: User) => {
    if (!realUser || target.jira_user_id === realUser.jira_user_id) return;
    await api.admin.impersonate.start(target.jira_user_id);
    setImpersonating(target);
  };

  const stopImpersonation = async () => {
    if (!impersonating) return;
    setImpersonating(null);
    setAuthIdentity({
      userId: IS_SSO_ENABLED ? null : (realUser?.jira_user_id ?? null),
      impersonating: null,
      googleToken: IS_SSO_ENABLED ? googleToken : null,
    });
    try { await api.admin.impersonate.stop(); } catch { /* best-effort */ }
  };

  const signOut = () => {
    sessionStorage.removeItem(SS_GOOGLE_TOKEN);
    setGoogleToken(null);
    setSsoUser(null);
    setImpersonating(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        realUser,
        isImpersonating: impersonating !== null,
        isLoading,
        needsLogin,
        isSsoEnabled: IS_SSO_ENABLED,
        sessionExpired,
        switchUser,
        startImpersonation,
        stopImpersonation,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
